import { T, useTranslate } from '@tolgee/react';
import { Collapse } from 'antd';
import { motion, useAnimation } from 'framer-motion';
import Link from 'next/link';
import router from 'next/router';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import styled from 'styled-components';

import { CheckSvg } from '../../../assets/icons';
import DownloadSvg from '../../../assets/icons/DownloadSvg';
import EmojiThanks from '../../../assets/icons/EmojiThanks';
import MagicStarsSvg from '../../../assets/icons/MagicStarsSvg';
import theme from '../../../assets/theme';
import useAccessBadge, {
  LicenseCoarseLevels,
  LicenseFineLevels,
} from '../../../hooks/useAccessBadge';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import useGeoLocation from '../../../hooks/useGeoLocation';
import { useMetrics } from '../../../hooks/useMetrics';
import useSignIn from '../../../hooks/useSignIn';
import BackendService from '../../../services/backendService';
import { configureStore } from '../../../utils/store';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import { NextPageWithLayout } from '../../layouts';
import { featureCompareTableData } from './utils';

export const FAQSection = ({
  showIndividualPremium = false,
}: {
  showIndividualPremium?: boolean;
}) => {
  const metrics = useMetrics(true);
  const { Panel } = Collapse;
  const items: {
    key: string;
    label: string | JSX.Element;
    content: JSX.Element;
  }[] = [
    {
      key: '0',
      label: (
        <T
          keyName="How do teaching sessions work on the free plan?"
          defaultValue="How do teaching sessions work on the free plan?"
        />
      ),
      content: (
        <p>
          <T
            keyName="A teaching session is counted each time you run a Curipod lesson with students in your classroom. Creating lessons, editing them, or previewing them on your own does not count as a teaching session. On the free plan, you have weekly teaching sessions that renew automatically every week. When you first sign up, you also receive bonus teaching sessions on top of your weekly limit, giving you extra room to explore Curipod. Your weekly sessions are always used first, so your bonus sessions are saved for weeks when you want to teach more. If you've used all your sessions for the week, you can still create and edit lessons, explore the library, and prepare for upcoming classes. Your sessions will renew the following week. For unlimited teaching sessions every week, your school can get the Curipod school plan - book a meeting with our team and we'll help you through the process."
            defaultValue="A teaching session is counted each time you run a Curipod lesson with students in your classroom. Creating lessons, editing them, or previewing them on your own does not count as a teaching session. On the free plan, you have weekly teaching sessions that renew automatically every week. When you first sign up, you also receive bonus teaching sessions on top of your weekly limit, giving you extra room to explore Curipod. Your weekly sessions are always used first, so your bonus sessions are saved for weeks when you want to teach more. If you've used all your sessions for the week, you can still create and edit lessons, explore the library, and prepare for upcoming classes. Your sessions will renew the following week. For unlimited teaching sessions every week, your school can get the Curipod school plan - book a meeting with our team and we'll help you through the process."
          />
        </p>
      ),
    },
    {
      key: '1',
      label: (
        <T
          keyName="How do I buy Curipod school site license?"
          defaultValue="How do I buy Curipod school site license?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Buying Curipod is easy! Fill out the"
            defaultValue="Buying Curipod is easy! Fill out the"
          />{' '}
          <Link href={'/quotes'}>
            <a
              onClick={() => {
                metrics.logEvent('PricingPage.faq.schoolLicenceForm.clicked');
              }}
              target="__blank"
              rel="noopener noreferrer"
            >
              <T keyName="quote request form" defaultValue="quote request form" />{' '}
            </a>
          </Link>
          .{' '}
          <T
            keyName="You'll hear back from us ASAP with a quote. We can set up your school plan as soon as we've recieved a signed quote or PO."
            defaultValue="You'll hear back from us ASAP with a quote. We can set up your school plan as soon as we've recieved a signed quote or PO."
          />
          <br />
          <br />
          <T
            keyName="Curipod site licenses can be purchased with credit card (we'll send you a link) or by mailing a check."
            defaultValue="Curipod site licenses can be purchased with credit card (we'll send you a link) or by mailing a check."
          />
        </p>
      ),
    },
    {
      key: '2',
      label: (
        <T
          keyName="Can I buy a license for X number of teachers?"
          defaultValue="Can I buy a license for X number of teachers?"
        />
      ),
      content: (
        <p>
          <T
            keyName="No. Curipod is only available through either School Site License or District License. We keep our prices as affordable as possible. We do offer special pricing for small schools (less than 200 students) and work with schools with budget constraints."
            defaultValue="No. Curipod is only available through either School Site License or District License. We keep our prices as affordable as possible. We do offer special pricing for small schools (less than 200 students) and work with schools with budget constraints."
          />
          <br />
          <br />
          <T
            keyName="We don't want to price to be a barrier for accessing Curipod and bringing curiosity to your classroom. If you would like more information about our pricing options and making Curipod accessible for you, please reach out to"
            defaultValue="We don't want to price to be a barrier for accessing Curipod and bringing curiosity to your classroom. If you would like more information about our pricing options and making Curipod accessible for you, please reach out to"
          />{' '}
          <a
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailW8BENQuestions.clicked');
            }}
            style={{ color: theme.colors.blue }}
            href="mailto:lexi@curipod.com"
          >
            lexi@curipod.com
          </a>
          .{' '}
          <T
            keyName="We're happy to work with you to find a solution."
            defaultValue="We're happy to work with you to find a solution."
          />
        </p>
      ),
    },
    {
      key: '3',
      label: <T keyName="How can I pay?" defaultValue="How can I pay?" />,
      content: (
        <p>
          <T
            keyName="We accept online payment - we will send you a link if that's your preferred method."
            defaultValue="We accept online payment - we will send you a link if that's your preferred method."
          />
          <br />
          <br />
          <T
            keyName="Curipod can also be purchased via check with an invoice. You can learn more about Curipod's payment options at"
            defaultValue="Curipod can also be purchased via check with an invoice. You can learn more about Curipod's payment options at"
          />{' '}
          <a href="https://curipod.notion.site/Curipod-Payment-Document-c18013fc71594bf2ab8b49f276c9d54e?pvs=4">
            <T keyName="this page" defaultValue="this page" />
          </a>
          .
        </p>
      ),
    },
    {
      key: '5',
      label: <T keyName="Do you have a W-9?" defaultValue="Do you have a W-9?" />,
      content: (
        <p>
          <T keyName="YES!" defaultValue="YES!" />
          <br />
          <br />
          <T
            keyName="You can also access Curipod's W-9 here:"
            defaultValue="You can also access Curipod's W-9 here:"
          />
          <br />
          <a
            style={{ textDecoration: 'none' }}
            href="https://drive.google.com/uc?export=download&id=1FkY9xPB7DRLAxm3ujfUb9SI1Qg-OGN0N"
            target="__blank"
            rel="noopener noreferrer"
            onClick={() => {
              metrics.logEvent('PricingPage.faq.W9Download.clicked');
            }}
          >
            <Button
              styles={{ border: 'none', boxShadow: 'none' }}
              type="ghost"
              icon={<DownloadSvg />}
            >
              <T keyName="Form W-9 Curipod.pdf" defaultValue="Form W-9 Curipod.pdf" />
            </Button>
          </a>
          <br />
          <T keyName="Email" defaultValue="Email" />{' '}
          <a
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailW8BENQuestions.clicked');
            }}
            style={{ color: theme.colors.blue }}
            href="mailto:lexi@curipod.com"
          >
            lexi@curipod.com
          </a>{' '}
          <T
            keyName="if you have any questions about this."
            defaultValue="if you have any questions about this."
          />
        </p>
      ),
    },
    {
      key: '6',
      label: (
        <T
          keyName="Do you offer any discounts or other incentives?"
          defaultValue="Do you offer any discounts or other incentives?"
        />
      ),
      content: (
        <p>
          <T
            keyName="We want to see students engaged through Curipod in classrooms all around the world, so we work hard to keep our pricing affordable."
            defaultValue="We want to see students engaged through Curipod in classrooms all around the world, so we work hard to keep our pricing affordable."
          />
          <br />
          <br />
          <T
            keyName="We have wonderful partnerships managers who will work with you to make Curipod a solution that fits into your school or district's custom needs."
            defaultValue="We have wonderful partnerships managers who will work with you to make Curipod a solution that fits into your school or district's custom needs."
          />
          <br />
          <br />
          <T
            keyName="We also recognize that there are budgetary constraints. We are happy to work with you to make Curipod affordable for your school or district. We offer the following options:"
            defaultValue="We also recognize that there are budgetary constraints. We are happy to work with you to make Curipod affordable for your school or district. We offer the following options:"
          />
          <ul>
            <li>
              <strong>
                <T
                  keyName="Small School Discount:"
                  defaultValue="Small School Discount:"
                />
              </strong>{' '}
              <T
                keyName="If your school is less than 200 students, we have a small school price for you. Email lexi@curipod.com for a custom quote."
                defaultValue="If your school is less than 200 students, we have a small school price for you. Email lexi@curipod.com for a custom quote."
              />
            </li>
            <li>
              <strong>
                <T
                  keyName="Financial Aid Discounts:"
                  defaultValue="Financial Aid Discounts:"
                />
              </strong>{' '}
              <T
                keyName="We don't want money to be a barrier to accessing Curipod! Please contact us if your school cannot afford Curipod. We are happy to connect with your administration so that we can find a way for you to access Curipod premium features. Email lexi@curipod.com for support."
                defaultValue="We don't want money to be a barrier to accessing Curipod! Please contact us if your school cannot afford Curipod. We are happy to connect with your administration so that we can find a way for you to access Curipod premium features. Email lexi@curipod.com for support."
              />
            </li>
            <li>
              <strong>
                <T
                  keyName="Grants & Funding Options:"
                  defaultValue="Grants & Funding Options:"
                />
              </strong>{' '}
              <T
                keyName="Our partnerships team are experts that understanding budgets and helping you fit Curipod into funding sources. Email lexi@curipod.com for support."
                defaultValue="Our partnerships team are experts that understanding budgets and helping you fit Curipod into funding sources. Email lexi@curipod.com for support."
              />
            </li>
          </ul>
        </p>
      ),
    },
    {
      key: '7',
      label: (
        <T keyName="How do you process data?" defaultValue="How do you process data?" />
      ),
      content: (
        <p>
          <T
            keyName="We have a page dedicated this, which you can"
            defaultValue="We have a page dedicated this, which you can"
          />{' '}
          <Link href={'/c/state-and-district-privacy-agreements'}>
            <a
              onClick={() => {
                metrics.logEvent(
                  'PricingPage.faq.stateAndDistrictPrivacyAgreements.clicked',
                );
              }}
            >
              <T keyName="check out here" defaultValue="check out here" />
            </a>
          </Link>
          .{' '}
          <T
            keyName="Data privacy is something we take very seriously."
            defaultValue="Data privacy is something we take very seriously."
          />
          <ul>
            <li>
              <T
                keyName="We are FERPA and COPPA compliant."
                defaultValue="We are FERPA and COPPA compliant."
              />
            </li>
            <li>
              <T
                keyName="Only teachers are allowed to have Curipod accounts, students join with a pin code."
                defaultValue="Only teachers are allowed to have Curipod accounts, students join with a pin code."
              />
            </li>
            <li>
              <T
                keyName="We sign data privacy agreements with schools and districts. We sign data privacy agreements with schools and districts. Reach out to jens@curipod.com if you need a DPA signed."
                defaultValue="We sign data privacy agreements with schools and districts. We sign data privacy agreements with schools and districts. Reach out to jens@curipod.com if you need a DPA signed."
              />
            </li>
          </ul>
          <T
            keyName="Email jens@curipod.com (CEO & Co-Founder) if you have questions about how Curipod processes data."
            defaultValue="Email jens@curipod.com (CEO & Co-Founder) if you have questions about how Curipod processes data."
          />
        </p>
      ),
    },
    {
      key: '8',
      label: (
        <T
          keyName="Can you sign our data privacy agreement?"
          defaultValue="Can you sign our data privacy agreement?"
        />
      ),
      content: (
        <p>
          <T
            keyName="Absolutely! We have a high demand for data privacy agreements and do our best to process them in a timely order. As a result, we give priority to those interested in purchasing a Curipod school or district license."
            defaultValue="Absolutely! We have a high demand for data privacy agreements and do our best to process them in a timely order. As a result, we give priority to those interested in purchasing a Curipod school or district license."
          />
          <br />
          <br />
          <T keyName="Please fill out the" defaultValue="Please fill out the" />{' '}
          <Link href={'https://share.hsforms.com/1Nwgx-5qBT_euEVjxG-eztw4evga'}>
            <a
              onClick={() => {
                metrics.logEvent('PricingPage.faq.signDataPrivacy.clicked');
              }}
              target="__blank"
              rel="noopener noreferrer"
            >
              <T keyName="form here" defaultValue="form here" />
            </a>
          </Link>
          . <T keyName="You can email" defaultValue="You can email" />{' '}
          <a
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailDataPrivacyQuestions.clicked');
            }}
            style={{ color: theme.colors.blue }}
            href="mailto:jens@curipod.com"
          >
            jens@curipod.com
          </a>{' '}
          <T keyName="with any questions." defaultValue="with any questions." />
        </p>
      ),
    },
    {
      key: '9',
      label: (
        <T
          keyName="What's the difference between monthly and yearly billing?"
          defaultValue="What's the difference between monthly and yearly billing?"
        />
      ),
      content: (
        <p>
          <T
            keyName="You can choose between monthly billing at $24/month or yearly billing at $19/month (billed annually at $228/year). With yearly billing, you save 21% compared to monthly billing."
            defaultValue="You can choose between monthly billing at $24/month or yearly billing at $19/month (billed annually at $228/year). With yearly billing, you save 21% compared to monthly billing."
          />
        </p>
      ),
    },
    {
      key: '10',
      label: <T keyName="Other questions?" defaultValue="Other questions?" />,
      content: (
        <p>
          <T keyName="Send an email to" defaultValue="Send an email to" />{' '}
          <a
            onClick={() => {
              metrics.logEvent('PricingPage.faq.emailOtherQuestions.clicked');
            }}
            style={{ color: theme.colors.blue }}
            href="mailto:lexi@curipod.com"
          >
            lexi@curipod.com
          </a>
          .{' '}
          <T
            keyName="We're happy to answer any questions you might have!"
            defaultValue="We're happy to answer any questions you might have!"
          />
        </p>
      ),
    },
  ];

  const filteredItems = showIndividualPremium
    ? items
    : items.filter(item => item.key !== '9');

  return (
    <GreenWrapper $isLandingPage={true}>
      <CenteredSection>
        <Title style={{ color: theme.colors.white }}>
          <T
            keyName="Frequently asked questions"
            defaultValue="Frequently asked questions"
          />
        </Title>
        <FAQCollapse accordion>
          {filteredItems.map(item => (
            <Panel header={item.label} key={item.key}>
              {item.content}
            </Panel>
          ))}
        </FAQCollapse>
      </CenteredSection>
    </GreenWrapper>
  );
};

const FAQCollapse = styled(Collapse)`
  font-size: 18px;
  font-family: 'Nunito';
  border: none;
  padding: 20px;
  background: ${theme.colors.primary};

  p {
    margin: 0;
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
  svg {
    width: 18px;
    height: 18px;
  }
  .ant-collapse-item .ant-collapse-header {
    color: ${theme.colors.white};
    font-weight: bold;
    font-size: 20px;
  }

  .ant-collapse-item {
    border: none;
    border-bottom: 1px solid #ffffff90;
    border-radius: 0px !important;
  }
  .ant-collapse-item.ant-collapse-item.ant-collapse-item-active {
    border-bottom: none;
  }

  .ant-collapse-content {
    border: none;
    border-left: 4px solid ${theme.colors.orange};
    border-radius: 10px;
  }

  .ant-collapse-item:last-child > .ant-collapse-content {
    border-radius: 10px;
  }
`;

const inspiration = [
  {
    title: (
      <T
        keyName="From New Teachers-  Saving Time:"
        defaultValue="From New Teachers-  Saving Time:"
      />
    ),
    quote: (
      <T
        keyName="I am a new teacher and am having to build TWO curriculums for my school. Curipod has helped me save so much time in getting our daily activities together so THANK YOU!!!"
        defaultValue="I am a new teacher and am having to build TWO curriculums for my school. Curipod has helped me save so much time in getting our daily activities together so THANK YOU!!!"
      />
    ),
    name: <T keyName="Teacher at Paragon Prep" defaultValue="Teacher at Paragon Prep" />,
  },
  {
    title: (
      <T keyName="The Students Experience:" defaultValue="The Students Experience:" />
    ),
    quote: (
      <T
        keyName="My experience with Curipod was pretty fun. It was a fun escape from boring work. When we could be doing boring SEL, Ms. Garcia would hop on Curipod. My face would light up. Curipod helped me improve my writing as well. I really love that Curipod has a drawing option. I can express my art and show off to my classmates. My favorite part is that it doesn't show your names."
        defaultValue="My experience with Curipod was pretty fun. It was a fun escape from boring work. When we could be doing boring SEL, Ms. Garcia would hop on Curipod. My face would light up. Curipod helped me improve my writing as well. I really love that Curipod has a drawing option. I can express my art and show off to my classmates. My favorite part is that it doesn't show your names."
      />
    ),
    name: (
      <T
        keyName="6th Grade Student from Garcia Middle School"
        defaultValue="6th Grade Student from Garcia Middle School"
      />
    ),
  },
  {
    title: <T keyName="The Student Engagement:" defaultValue="The Student Engagement:" />,
    quote: (
      <T
        keyName="I never have to beg my students to participate if it's in a Curipod. They hear the music start, and they are there."
        defaultValue="I never have to beg my students to participate if it's in a Curipod. They hear the music start, and they are there."
      />
    ),
    name: (
      <T
        keyName="Christie Cloud, Coweta County Schools"
        defaultValue="Christie Cloud, Coweta County Schools"
      />
    ),
  },
  {
    title: (
      <T keyName="The Students Experience:" defaultValue="The Students Experience:" />
    ),
    quote: (
      <T
        keyName="My experience using the Curipod platform is the best because you can create your opinions and AI gives you feedback about how you can make your writing better."
        defaultValue="My experience using the Curipod platform is the best because you can create your opinions and AI gives you feedback about how you can make your writing better."
      />
    ),
    name: (
      <T
        keyName="6th Grade Student from Garcia Middle School"
        defaultValue="6th Grade Student from Garcia Middle School"
      />
    ),
  },
  {
    title: <T keyName="On Student Engagement:" defaultValue="On Student Engagement:" />,
    quote: (
      <T
        keyName="The most important thing is that the students loved it and it was a way to get every student involved in the lesson."
        defaultValue="The most important thing is that the students loved it and it was a way to get every student involved in the lesson."
      />
    ),
    name: <T keyName="Curipod First Time User" defaultValue="Curipod First Time User" />,
  },
  {
    title: <T keyName="On Student Engagement:" defaultValue="On Student Engagement:" />,
    quote: (
      <T
        keyName="OMG this is amazing! So engaging I am in tears."
        defaultValue="OMG this is amazing! So engaging I am in tears."
      />
    ),
    name: <T keyName="Curipod First Time User" defaultValue="Curipod First Time User" />,
  },
  {
    title: (
      <T
        keyName="On the Teaching Experience:"
        defaultValue="On the Teaching Experience:"
      />
    ),
    quote: (
      <T
        keyName="It was an amazing teaching experience. My students were engaged on slides that involved conversation. They were using language taught in that content area. They loved being able to respond to a variety of questions. They all voted to use this again."
        defaultValue="It was an amazing teaching experience. My students were engaged on slides that involved conversation. They were using language taught in that content area. They loved being able to respond to a variety of questions. They all voted to use this again."
      />
    ),
    name: (
      <T
        keyName="Teacher at Portsmouth Public Schools"
        defaultValue="Teacher at Portsmouth Public Schools"
      />
    ),
  },
  {
    title: (
      <T
        keyName="On AI Generation - Saving Time:"
        defaultValue="On AI Generation - Saving Time:"
      />
    ),
    quote: (
      <T
        keyName="I liked that I didn't have to come up with a lesson, I just typed it in and AI developed a review for me for my Fossil Fuels unit. All the information was accurate to what I had taught during the unit! It was awesome."
        defaultValue="I liked that I didn't have to come up with a lesson, I just typed it in and AI developed a review for me for my Fossil Fuels unit. All the information was accurate to what I had taught during the unit! It was awesome."
      />
    ),
    name: <T keyName="Curipod First Time User" defaultValue="Curipod First Time User" />,
  },
  {
    title: <T keyName="On AI feedback:" defaultValue="On AI feedback:" />,
    quote: (
      <T
        keyName="AI feedback is a game changer…. The experience was mind blowing"
        defaultValue="AI feedback is a game changer…. The experience was mind blowing"
      />
    ),
    name: <T keyName="Curipod First Time User" defaultValue="Curipod First Time User" />,
  },
];

const TeacherInspiration = () => {
  const currentInspirationIndex = useRef<number>(0);
  const [renderedInspirationIndex, setRenderedInspirationIndex] = useState<number>(0);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const handleSetCurrentInspirationIndex = (index: number) => {
    if (index >= inspiration.length - 1) {
      currentInspirationIndex.current = 0;
      setRenderedInspirationIndex(0);
      return;
    }
    currentInspirationIndex.current = index;
    setRenderedInspirationIndex(index);
  };
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const newInterval = setInterval(() => {
      handleSetCurrentInspirationIndex(currentInspirationIndex.current + 1);
    }, 10000);
    if (!intervalRef.current) intervalRef.current = newInterval;
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <CenteredSection>
      <Title>
        <T
          keyName="See what teachers & students are saying"
          defaultValue="See what teachers & students are saying"
        />
      </Title>

      <TeacherInspirationContainer>
        <TecherInspirationContainerQuotes>
          {inspiration.map((inspo, i) => {
            return (
              <TeacherInspirationWrapper
                $isVisible={renderedInspirationIndex === i}
                key={i}
              >
                <div key={i}>
                  <InspoTitle>{inspo.title}</InspoTitle>
                  <TeacherInspirationDiv>
                    <p style={{ textAlign: 'left' }}>&ldquo;{inspo.quote}&rdquo;</p>
                    <p style={{ fontWeight: 400, textAlign: 'left', fontSize: 18 }}>
                      {inspo.name}
                    </p>
                  </TeacherInspirationDiv>
                </div>
              </TeacherInspirationWrapper>
            );
          })}
        </TecherInspirationContainerQuotes>

        <Dots>
          {inspiration.map((inspo, i) => {
            return (
              <DotButton
                key={i}
                $isActive={renderedInspirationIndex === i}
                onClick={() => {
                  handleSetCurrentInspirationIndex(i);
                }}
              ></DotButton>
            );
          })}
        </Dots>
      </TeacherInspirationContainer>
    </CenteredSection>
  );
};

const TeacherInspirationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0;
  position: relative;
  height: 400px;

  @media (max-width: 550px) {
    padding: 0px 10px;
  }
`;
const TecherInspirationContainerQuotes = styled.div`
  display: flex;
  justify-content: center;
  height: 500px;
`;
const TeacherInspirationWrapper = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  padding: 20px;
  width: 100%;
  justify-items: start;
  gap: 20px;
  display: flex;
  justify-content: center;
  max-height: 500px;

  ${({ $isVisible }) =>
    $isVisible
      ? `
    opacity: 1;
    visibility: visible;
    transition: opacity 0.5s ease-in-out;
  `
      : `
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.5s ease-in-out;
  `}

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    padding: 0px 10px;
  }

  @media (max-width: 550px) {
    h1 {
      font-size: 20px;
    }

    p {
      font-size: 16px !important;
    }
  }
`;

const TeacherInspirationDiv = styled.div`
  max-width: 600px;
  width: 100%;

  p {
    font-size: 20px;
    font-weight: 300;
  }
`;

const InspoTitle = styled.h1`
  font-size: 24px;
  text-align: left;
  margin-bottom: 10px;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  width: 100%;
`;

const DotButton = styled(Button)<{ $isActive: boolean }>`
  visibility: visible !important;
  margin: 0 5px;
  width: 10px !important;
  height: 10px;
  border-radius: 50%;
  padding: 0px;
  background-color: ${theme.colors.greyDark};
  background-color: ${({ $isActive }) =>
    $isActive ? theme.colors.green : theme.colors.grey};
`;

export const HighlightsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  padding: 10px;
  gap: 5px;
  width: 100%;
  margin-top: 20px;
  background-color: ${theme.colors.greyLight};
  border-radius: 10px;

  h4 {
    font-size: 18px;
    margin-bottom: 10px;
  }
  p {
    margin: 0;
  }
`;

const SchoolAndDistrictItemContainer = styled(motion.div)`
  display: grid;
  grid-template-rows: 42px 60px 60px 60px auto;
  padding: 20px;
  position: relative;
  flex: 1;
  height: 100%;
  justify-items: center;
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.15);
  background: white;
  color: ${theme.colors.black};
  width: 100%;

  h3 {
    font-size: 28px;
  }

  a,
  button {
    width: 100%;
  }

  @media (max-width: 600px) {
    grid-template-rows: 60px 60px 90px auto;
    h3 {
      font-size: 20px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  height: 100%;
  align-items: center;

  button {
    width: 100%;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;

    button {
      width: fit-content;
      margin: 0 auto;
    }
  }
`;

export const schoolAndDistrictHighlights = [
  {
    id: 'everything-plus',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T
            keyName="Everything on the free plan, plus"
            defaultValue="Everything on the free plan, plus"
          />
        </strong>
      </span>
    ),
  },
  {
    id: 'unlimited-usage',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T keyName="Unlimited Usage:" defaultValue="Unlimited Usage:" />
        </strong>{' '}
        <T
          keyName="Use all the powerful AI features in Curipod as much as you want, including AI feedback, lesson generation and translations."
          defaultValue="Use all the powerful AI features in Curipod as much as you want, including AI feedback, lesson generation and translations."
        />
      </span>
    ),
  },
  {
    id: 'unlimited-test-prep',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T keyName="Unlimited Test Prep:" defaultValue="Unlimited Test Prep:" />
        </strong>{' '}
        <T
          keyName="Unlimited standards-aligned test prep lesson generations."
          defaultValue="Unlimited standards-aligned test prep lesson generations."
        />
      </span>
    ),
  },
  {
    id: 'standards-alignment',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T
            keyName="Standards & Curriculum alignment:"
            defaultValue="Standards & Curriculum alignment:"
          />
        </strong>{' '}
        <T
          keyName="Curipod creates the lesson you need, when you need it—on any topic, in any language, and aligned to your standards and instructional materials."
          defaultValue="Curipod creates the lesson you need, when you need it—on any topic, in any language, and aligned to your standards and instructional materials."
        />
      </span>
    ),
  },
  {
    id: 'customize-ai-feedback',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T keyName="Customize AI feedback:" defaultValue="Customize AI feedback:" />
        </strong>{' '}
        <T
          keyName="Customize AI feedback using your own rubrics, and accommodate longer student responses."
          defaultValue="Customize AI feedback using your own rubrics, and accommodate longer student responses."
        />
      </span>
    ),
  },
  {
    id: 'student-reports',
    bullet: <CheckSvg />,
    text: (
      <span>
        <strong>
          <T keyName="Student Reports:" defaultValue="Student Reports:" />
        </strong>{' '}
        <T
          keyName="View student responses along with their AI-generated feedback, including a Glow and Grow summary that highlights strengths and areas for improvement. Instantly access insights into student learning to help guide your instruction."
          defaultValue="View student responses along with their AI-generated feedback, including a Glow and Grow summary that highlights strengths and areas for improvement. Instantly access insights into student learning to help guide your instruction."
        />
      </span>
    ),
  },
];

export const PricingPlans = ({
  isLandingPage,
  onProductSelected,
  showIndividualPremium,
  isUSPricing,
}: {
  isLandingPage?: boolean;
  isWorkspacePaywall?: boolean;
  onProductSelected?: (product: 'free' | 'premium' | 'enterprise' | 'schoolSite') => void;
  showIndividualPremium?: boolean;
  isUSPricing?: boolean;
}) => {
  const metrics = useMetrics(true);
  const { t } = useTranslate();
  const { signIn } = useSignIn();

  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [showAuthGuard, setShowAuthGuard] = useState(false);
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'yearly'>(
    'yearly',
  );

  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { config } = configureStore();

  const handleIndividualCheckout = useCallback(async () => {
    if (!isAuthenticated) {
      metrics.logEvent('PricingPage.IndividualPremium.AuthGuard.Prompted');
      setShowAuthGuard(true);
      return;
    }

    // Only allow checkout for Freemium users
    if (userAccessBadge?.license?.coarseLevel !== LicenseCoarseLevels.freemium) {
      console.warn('Checkout only available for Freemium users');
      return;
    }

    try {
      setIsCheckoutLoading(true);
      metrics.logEvent(`PricingPage.IndividualPremium.${billingFrequency}.clicked`);

      const isUSOrg = userAccessBadge?.organization?.country === 'US';
      const priceId =
        billingFrequency === 'yearly'
          ? isUSOrg
            ? config.stripe.yearlyUSIndividualPremiumPriceId
            : config.stripe.yearlyIndividualPremiumPriceId
          : isUSOrg
          ? config.stripe.monthlyUSIndividualPremiumPriceId
          : config.stripe.monthlyIndividualPremiumPriceId;

      const { url } = await BackendService.instance.users.createIndividualCheckoutSession(
        {
          priceId,
          successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing?checkout=canceled&priceId=${priceId}`,
        },
      );

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setIsCheckoutLoading(false);
    }
  }, [
    isAuthenticated,
    metrics,
    billingFrequency,
    config.stripe.yearlyIndividualPremiumPriceId,
    config.stripe.monthlyIndividualPremiumPriceId,
    config.stripe.yearlyUSIndividualPremiumPriceId,
    config.stripe.monthlyUSIndividualPremiumPriceId,
    userAccessBadge,
  ]);

  // Check for checkout callback on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldCheckout = urlParams.get('checkout');

    // Wait for access badge to be loaded before triggering checkout
    if (shouldCheckout === 'individual-premium' && isAuthenticated && userAccessBadge) {
      // Remove the query param
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Trigger checkout
      handleIndividualCheckout();
    }
  }, [isAuthenticated, userAccessBadge, handleIndividualCheckout]);

  const handleAuthGuardSignIn = () => {
    metrics.logEvent('PricingPage.IndividualPremium.AuthGuard.SignIn');
    const currentUrl = window.location.pathname + '?checkout=individual-premium';
    signIn({ callbackUrl: currentUrl });
  };

  const handleAuthGuardSignUp = () => {
    metrics.logEvent('PricingPage.IndividualPremium.AuthGuard.SignUp');
    const currentUrl = window.location.pathname + '?checkout=individual-premium';
    router.push({
      pathname: '/signup',
      query: { callback: currentUrl },
    });
  };

  const basicHighlights = [
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T
              keyName="AI-powered curriculum and instruction platform:"
              defaultValue="AI-powered curriculum and instruction platform:"
            />
          </strong>{' '}
          <T
            keyName="Teacher-paced lessons that get students to look up from their screens and talk. (Limited usage)"
            defaultValue="Teacher-paced lessons that get students to look up from their screens and talk. (Limited usage)"
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T keyName="Test Prep Access:" defaultValue="Test Prep Access:" />
          </strong>{' '}
          <T
            keyName="Includes 3 free standards-aligned test prep lessons."
            defaultValue="Includes 3 free standards-aligned test prep lessons."
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T
              keyName="Student Engagement Tool:"
              defaultValue="Student Engagement Tool:"
            />
          </strong>{' '}
          <T
            keyName="Includes polls, drawings, word clouds, open-ended questions, and AI feedback — designed to spark classroom dialogue and critical thinking."
            defaultValue="Includes polls, drawings, word clouds, open-ended questions, and AI feedback — designed to spark classroom dialogue and critical thinking."
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T
              keyName="Transform your existing slide decks:"
              defaultValue="Transform your existing slide decks:"
            />
          </strong>{' '}
          <T
            keyName="Turn them into interactive lessons where students actively engage."
            defaultValue="Turn them into interactive lessons where students actively engage."
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T keyName="Moderation Tool:" defaultValue="Moderation Tool:" />
          </strong>{' '}
          <T
            keyName="Keep the lesson on track while keeping students on-task and engaged. Monitor responses for those who need support and intervene in real time."
            defaultValue="Keep the lesson on track while keeping students on-task and engaged. Monitor responses for those who need support and intervene in real time."
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T keyName="Reports:" defaultValue="Reports:" />
          </strong>{' '}
          <T
            keyName="After each lesson, AI generates class-wide and student reports with insights on participation, understandings, and areas for growth. Quickly spot trends, identify students who need more support, and use real-time data to inform future instruction."
            defaultValue="After each lesson, AI generates class-wide and student reports with insights on participation, understandings, and areas for growth. Quickly spot trends, identify students who need more support, and use real-time data to inform future instruction."
          />
        </span>
      ),
    },
    {
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T keyName="Safe & Compliant:" defaultValue="Safe & Compliant:" />
          </strong>{' '}
          <T
            keyName="FERPA-, COPPA- and GDPR-compliant with student data privacy built in."
            defaultValue="FERPA-, COPPA- and GDPR-compliant with student data privacy built in."
          />
        </span>
      ),
    },
  ];

  const usHiddenHighlightIds = new Set([
    'unlimited-test-prep',
    'customize-ai-feedback',
    'student-reports',
  ]);

  const individualPremiumHighlights = [
    {
      id: 'everything-plus-individual',
      bullet: <CheckSvg />,
      text: (
        <span>
          <strong>
            <T
              keyName="Everything on the free plan, plus"
              defaultValue="Everything on the free plan, plus"
            />
          </strong>
        </span>
      ),
    },
    ...schoolAndDistrictHighlights
      .slice(1)
      .filter(h => !isUSPricing || !usHiddenHighlightIds.has(h.id)),
  ];

  return (
    <CenteredSection style={{ margin: !isLandingPage ? '0px auto' : '40px auto' }}>
      <BaseModal hideCancel open={showAuthGuard} width="500px" centered>
        <ModalContent>
          <IconContainer>
            <span style={{ color: '#666' }}>
              <EmojiThanks />
            </span>
          </IconContainer>
          <ModalTitle>
            <T
              keyName="You need to be logged in to upgrade"
              defaultValue="You need to be logged in to upgrade"
            />
          </ModalTitle>
          <ModalMessage>
            <T
              keyName="Please log in or create an account"
              defaultValue="Please log in or create an account"
            />
          </ModalMessage>
          <ModalButtonContainer>
            <Button
              type="ghost"
              onClick={handleAuthGuardSignIn}
              styles={{ marginRight: '12px' }}
            >
              <T keyName="Log in" defaultValue="Log in" />
            </Button>
            <Button
              type="primary"
              onClick={handleAuthGuardSignUp}
              styles={{ marginRight: '12px' }}
            >
              <T keyName="Sign up" defaultValue="Sign up" />
            </Button>
          </ModalButtonContainer>
        </ModalContent>
      </BaseModal>
      <Title>
        <T keyName="Our plans" defaultValue="Our plans" />
      </Title>
      <SchoolsAndDistrictsContainer
        style={{
          marginTop: isLandingPage ? 20 : 0,
          gridTemplateColumns: showIndividualPremium
            ? 'repeat(3, 1fr)'
            : 'repeat(2, 1fr)',
        }}
      >
        <AnimatedSchoolAndDistrictItemContainer
          onClick={() => onProductSelected && onProductSelected('free')}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              marginBottom: 0,
              height: 34,
            }}
          >
            <div
              style={{
                padding: '3px 20px',
                // border: `2px solid ${theme.colors.green}`,
                // borderRadius: 50,
                height: 34,
                display: 'flex',
                gap: 10,
              }}
            >
              {/* <IconWrapper>
                <MagicStarsSvg />
              </IconWrapper>
              <span style={{ fontWeight: 'bold' }}>Recommended</span> */}
            </div>
          </div>
          <h3>
            <T keyName="Free Plan" defaultValue="Free Plan" />
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              flex: 1,
            }}
          >
            <PriceContainer>
              <p>$0</p>
            </PriceContainer>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              width: '100%',
              height: '100%',
              alignItems: 'center',
            }}
          >
            {!isAuthenticated && (
              <Button
                onClick={() => router.push('/signup')}
                styles={{ width: 'fit-content', margin: '0 auto' }}
                type="ghost"
              >
                <T keyName="Start for free today" defaultValue="Start for free today" />
              </Button>
            )}
            {userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.freemium && (
              <p style={{ width: '100%', textAlign: 'center' }}>
                <T keyName="Your current plan" defaultValue="Your current plan" />
              </p>
            )}
          </div>
          <HighlightsContainer>
            <h4>
              <T keyName="Highlights" defaultValue="Highlights" />
            </h4>
            {basicHighlights.map((highlight, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  width: '100%',
                  alignItems: 'start',
                }}
              >
                <IconWrapper height={'18px'} width={'18px'}>
                  {highlight.bullet}
                </IconWrapper>
                <p>{highlight.text}</p>
              </div>
            ))}
          </HighlightsContainer>
        </AnimatedSchoolAndDistrictItemContainer>

        {showIndividualPremium && (
          <AnimatedSchoolAndDistrictItemContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                height: 38,
                marginBottom: 20,
              }}
            >
              <BillingToggle>
                <BillingOption
                  $isActive={billingFrequency === 'monthly'}
                  onClick={() => setBillingFrequency('monthly')}
                >
                  <T keyName="Monthly" defaultValue="Monthly" />
                </BillingOption>
                <BillingOption
                  $isActive={billingFrequency === 'yearly'}
                  onClick={() => setBillingFrequency('yearly')}
                >
                  <T keyName="Yearly" defaultValue="Yearly" />
                  <SaveBadge>
                    {isUSPricing ? (
                      <T keyName="Save 20%" defaultValue="Save 20%" />
                    ) : (
                      <T keyName="Save 21%" defaultValue="Save 21%" />
                    )}
                  </SaveBadge>
                </BillingOption>
              </BillingToggle>
            </div>
            <h3>
              <T keyName="Premium" defaultValue="Premium" />
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                flex: 1,
              }}
            >
              <PriceContainer>
                {isUSPricing ? (
                  billingFrequency === 'yearly' ? (
                    <p>
                      <T
                        keyName="pricing.us.yearly"
                        defaultValue="$20/month billed annually"
                      />
                    </p>
                  ) : (
                    <p>
                      <T keyName="pricing.us.monthly" defaultValue="$25/month" />
                    </p>
                  )
                ) : billingFrequency === 'yearly' ? (
                  <p>
                    <T
                      keyName="$19/month billed annually"
                      defaultValue="$19/month billed annually"
                    />
                  </p>
                ) : (
                  <p>
                    <T keyName="$24/month" defaultValue="$24/month" />
                  </p>
                )}
              </PriceContainer>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 10,
                width: '100%',
                height: '100%',
                alignItems: 'center',
              }}
            >
              {!isAuthenticated && (
                <Button
                  onClick={handleIndividualCheckout}
                  loading={isCheckoutLoading}
                  styles={{ width: 'fit-content', margin: '0 auto' }}
                >
                  <T keyName="Upgrade to Premium" defaultValue="Upgrade to Premium" />
                </Button>
              )}
              {isAuthenticated && userAccessBadge?.license?.coarseLevel === 'Freemium' && (
                <Button
                  onClick={handleIndividualCheckout}
                  loading={isCheckoutLoading}
                  styles={{ width: 'fit-content', margin: '0 auto' }}
                >
                  <T keyName="Upgrade to Premium" defaultValue="Upgrade to Premium" />
                </Button>
              )}
              {isAuthenticated && userAccessBadge?.license?.coarseLevel !== 'Freemium' && (
                <p style={{ width: '100%', textAlign: 'center' }}>
                  <T keyName="Your current plan" defaultValue="Your current plan" />
                </p>
              )}
            </div>
            <HighlightsContainer>
              <h4>
                <T keyName="Highlights" defaultValue="Highlights" />
              </h4>
              {individualPremiumHighlights.map((highlight, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    width: '100%',
                    alignItems: 'start',
                  }}
                >
                  <IconWrapper height={'18px'} width={'18px'}>
                    {highlight.bullet}
                  </IconWrapper>
                  <p>{highlight.text}</p>
                </div>
              ))}
            </HighlightsContainer>
          </AnimatedSchoolAndDistrictItemContainer>
        )}

        <AnimatedSchoolAndDistrictItemContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              height: 38,
            }}
          >
            <div
              style={{
                padding: '3px 20px',
                border: `2px solid ${theme.colors.orange}`,
                background: theme.colors.orange,
                borderRadius: 50,
                height: 34,
                display: 'flex',
                gap: 10,
              }}
            >
              <IconWrapper style={{ cursor: 'default' }} color="white">
                <MagicStarsSvg />
              </IconWrapper>
              <span style={{ fontWeight: 'bold', fontSize: 16, color: 'white' }}>
                <T keyName="Recommended" defaultValue="Recommended" />
              </span>
            </div>
          </div>
          <h3>
            <T keyName="School & District Plan" defaultValue="School & District Plan" />
          </h3>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              flex: 1,
            }}
          >
            <PriceContainer>
              <p>
                <T keyName="Custom price" defaultValue="Custom price" />
              </p>
            </PriceContainer>
          </div>
          {(userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise ||
            userAccessBadge?.license.fineLevel !== LicenseFineLevels.trial) && (
            <ButtonContainer>
              {/* <Link href={'/quotes'}>
                <a
                  onClick={() => {
                    metrics.logEvent('PricingPage.GetASchoolQuote.clicked');
                    trackEvent('GetASchoolQuoteClicked');
                  }}
                >
                  <Button>Get a school quote</Button>
                </a>
              </Link> */}
              <Link href={'/quotes'}>
                <a
                  onClick={() => {
                    metrics.logEvent('PricingPage.GetADistrictQuote.clicked');
                  }}
                >
                  <Button>
                    <T keyName="Request a quote" defaultValue="Request a quote" />
                  </Button>
                </a>
              </Link>
              {showIndividualPremium && (
                <a
                  href="https://calendly.com/d/cm5c-5db-qpm/curipod-meeting"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    metrics.logEvent('PricingPage.BookAMeeting.clicked');
                  }}
                >
                  <Button type="ghost">
                    <T keyName="Book a meeting" defaultValue="Book a meeting" />
                  </Button>
                </a>
              )}
            </ButtonContainer>
          )}
          {userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise && (
            <p style={{ width: '100%', textAlign: 'center' }}>
              <T keyName="Your current plan" defaultValue="Your current plan" />
            </p>
          )}
          <HighlightsContainer>
            <h4>
              <T keyName="Highlights" defaultValue="Highlights" />
            </h4>
            {schoolAndDistrictHighlights.map((highlight, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  width: '100%',
                  alignItems: 'start',
                }}
              >
                <IconWrapper height={'18px'} width={'18px'}>
                  {highlight.bullet}
                </IconWrapper>
                <p>{highlight.text}</p>
              </div>
            ))}
          </HighlightsContainer>
        </AnimatedSchoolAndDistrictItemContainer>
      </SchoolsAndDistrictsContainer>
    </CenteredSection>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 10px;
  background-color: ${theme.colors.white};
  border-radius: 10px;
  shadow: 0px 2px 20px rgba(0, 0, 0, 0.15);
`;
const TableContainer = styled.div<{ $showIndividualPremium?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $showIndividualPremium }) =>
    $showIndividualPremium ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'};
  gap: 0px;
  width: 100%;
  padding: 10px;

  justify-items: center;

  p {
    margin: 0;
    padding: 10px;
    font-weight: 400;
    text-align: center;
    border-bottom: 1px solid ${theme.colors.grey};
    position: relative;
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    text-align: left;

    strong {
      text-align: left;
    }
  }

  h2 {
    font-size: 24px;
    margin-bottom: 0px;
    justify-self: start;
    width: 100%;
    border-bottom: 2px solid ${theme.colors.grey};
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;

    p,
    h2,
    h3 {
      border-bottom: 1px solid ${theme.colors.grey};
    }
  }
`;

const FeatureGroupHeading = styled.h3`
  font-size: 20px;
  margin-bottom: 10px;
  width: 100%;
  border-bottom: 2px solid ${theme.colors.grey};
  text-align: center;
  align-self: end;
  @media (max-width: 700px) {
    font-size: 18px !important;
  }
`;

export const CompareAllFeaturesTable = ({
  showIndividualPremium = false,
}: {
  showIndividualPremium?: boolean;
}) => {
  return (
    <CenteredSection style={{ marginBottom: 5 }}>
      <Title style={{ textAlign: 'center' }}>
        <T keyName="Compare all features" defaultValue="Compare all features" />
      </Title>
      <Wrapper>
        {featureCompareTableData.map((featureGroup, i) => (
          <TableContainer key={i} $showIndividualPremium={showIndividualPremium}>
            <FeatureGroupHeading style={{ textAlign: 'left', fontSize: 24 }}>
              {featureGroup.heading}
            </FeatureGroupHeading>
            <FeatureGroupHeading>
              <T keyName="Free" defaultValue="Free" />
            </FeatureGroupHeading>
            {showIndividualPremium && (
              <FeatureGroupHeading>
                <T keyName="Premium" defaultValue="Premium" />
              </FeatureGroupHeading>
            )}
            <FeatureGroupHeading>
              <T keyName="School & District" defaultValue="School & District" />
            </FeatureGroupHeading>

            {featureGroup.rows.map(row => (
              <>
                <p style={{ width: '100%', alignItems: 'start', display: 'block' }}>
                  {row.feature}
                </p>
                <p style={{ width: '100%' }}>{row.basic}</p>
                {showIndividualPremium && <p style={{ width: '100%' }}>{row.premium}</p>}
                <p style={{ width: '100%' }}>{row.schoolPlan}</p>
              </>
            ))}
          </TableContainer>
        ))}
      </Wrapper>
    </CenteredSection>
  );
};

export const AnimatedSchoolAndDistrictItemContainer = ({
  onClick,
  children,
}: {
  children?: React.ReactNode;
  onClick?: () => void;
}) => {
  const animation = useAnimation();

  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };
  return (
    <SchoolAndDistrictItemContainer
      animate={animation}
      onHoverStart={hoverSequence}
      onClick={() => onClick && onClick()}
    >
      {children}
    </SchoolAndDistrictItemContainer>
  );
};

export const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  p {
    font-size: 20px;
    font-weight: bold;
    margin: 0;
  }
  span {
    font-size: 14px;
    font-weight: bold;
    margin: -10px 0 0 0;
    color: ${theme.colors.iconGrey};
  }
  @media (max-width: 600px) {
    p {
      font-size: 24px;
    }
  }
`;

const SchoolsAndDistrictsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  align-items: center;
  max-width: 1400px;
  margin: 10px auto;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr !important;
    max-width: 600px;
  }
`;

const Title = styled.h2`
  font-weight: bold;
  width: 100%;
  text-align: center;
  font-size: 36px;

  @media (max-width: 900px) {
    font-size: 24px;
  }
`;

const Container = styled.div`
  width: 100%;
`;

const GreenWrapper = styled.div<{ $isLandingPage?: boolean }>`
  background: ${theme.colors.green};
  background: ${({ $isLandingPage }) =>
    $isLandingPage ? theme.colors.green : theme.colors.background};
  width: 100%;
  padding: 1px 0px;
`;

const CenteredSection = styled.div`
  width: 100%;
  max-width: 1128px;
  margin-bottom: 30px;
  margin-top: 40px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 1128px) {
    max-width: 100%;
    padding: 0px 20px !important;
  }

  @media (max-width: 500px) {
    padding: 5px !important;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
`;

const IconContainer = styled.div`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
`;

const ModalMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 30px;
  color: #666;
  text-align: center;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const BillingToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  width: 100%;
`;

const BillingToggle = styled.div`
  display: flex;
  gap: 0;
  background-color: ${theme.colors.greyLight};
  border-radius: 50px;
  padding: 4px;
  width: fit-content;
`;

const BillingOption = styled.button<{ $isActive: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 50px;
  background-color: ${({ $isActive }) =>
    $isActive ? theme.colors.white : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? theme.colors.black : theme.colors.iconGrey)};
  font-weight: ${({ $isActive }) => ($isActive ? 'bold' : 'normal')};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0px 2px 8px rgba(0, 0, 0, 0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

const SaveBadge = styled.span`
  background-color: ${theme.colors.green};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
`;

const PricingPageContent: NextPageWithLayout = () => {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const session = useSession();
  const showIndividualPremiumUnauthenticated = useFeatureFlag(
    'show_individual_premium_unauthenticated',
  );
  const internationalIndividualPremium = useFeatureFlag(
    'international_individual_premium',
  );
  const isAuthenticated = session.status === 'authenticated';
  const { isUS, isLoading: isGeoLoading, error: geoError } = useGeoLocation();

  // Wait for geo loading to finish before determining whether to show individual premium
  // This prevents flashing of the individual premium plan for US users
  // If geo lookup failed, treat as US (conservative default) to avoid incorrectly showing individual premium
  const showIndividualPremium =
    !isGeoLoading &&
    !geoError &&
    !!internationalIndividualPremium &&
    ((!isAuthenticated && !isUS && showIndividualPremiumUnauthenticated) ||
      userAccessBadge?.canPurchaseIndividualPremium);

  const isUSPricing = isAuthenticated
    ? userAccessBadge?.organization?.country === 'US'
    : isUS;

  return (
    <Container style={{ marginTop: 40 }}>
      <PricingPlans
        isLandingPage
        showIndividualPremium={showIndividualPremium}
        isUSPricing={isUSPricing}
      />
      <CompareAllFeaturesTable showIndividualPremium={showIndividualPremium} />
      <TeacherInspiration />
      <FAQSection showIndividualPremium={showIndividualPremium} />
    </Container>
  );
};

export default PricingPageContent;
